// Trustpilot-style star rating: five square tiles with a white star glyph.
//
// Interactive mode is a native radio group — each tile is a <label> wrapping a
// visually-hidden <input type="radio">, so clicking anywhere on the tile
// selects it, arrow keys move/select for free, and screen readers announce
// "3 stars, radio, 3 of 5". Hovering previews tiles 1..i in tier i's color.
//
// readOnly renders the same tiles with no inputs (EMR lists, the thank-you
// card) as a single role="img" so the rating is announced once, not five times.
//
// Shared between the patient portal and the EMR bundle; colors come from
// ~/shared/styles/ratingPalette.

import { makeStyles } from "@mui/styles";
import { useState } from "react";

import {
  RATING_COLORS,
  RATING_MOBILE_MEDIA_QUERY,
  VISUALLY_HIDDEN,
  starTierColor,
} from "~/shared/styles/ratingPalette";

const STAR_COUNT = 5;
const STARS = [1, 2, 3, 4, 5];

// Five-point star on a 24x24 grid (spans x 1.6–22.4, y 1.6–21.3). Also used
// by the thank-you page's decorative cluster.
export const STAR_PATH =
  "M12 1.6l3.14 6.62 7.26.9-5.34 5.02 1.38 7.2L12 17.8l-6.44 3.54 1.38-7.2L1.6 9.12l7.26-.9z";

const useStyles = makeStyles({
  group: {
    display: "inline-flex",
    verticalAlign: "middle",
  },
  label: {
    position: "relative",
    display: "block",
    margin: 0,
    padding: 0,
    lineHeight: 0,
    cursor: "pointer",
    WebkitTapHighlightColor: "transparent",
  },
  // Visually hidden but still focusable/clickable (never display:none — that
  // would drop the radio out of the tab order and the label activation).
  input: {
    ...VISUALLY_HIDDEN,
    "&:focus-visible + $tile": {
      outline: `2px solid ${RATING_COLORS.accent}`,
      outlineOffset: "2px",
    },
  },
  // Trustpilot's tiles are square — no radius.
  tile: {
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "var(--star-tile-size)",
    height: "var(--star-tile-size)",
    borderRadius: 0,
    transition: "background-color 120ms ease",
    [RATING_MOBILE_MEDIA_QUERY]: {
      width: "var(--star-tile-size-mobile, var(--star-tile-size))",
      height: "var(--star-tile-size-mobile, var(--star-tile-size))",
    },
  },
  // The star itself is ~66% of the tile on Trustpilot; STAR_PATH only fills
  // ~87% of its viewBox, so the svg box is 76% to land there.
  glyph: {
    display: "block",
    width: "76%",
    height: "76%",
  },
});

function Tile({ classes, active, color }) {
  return (
    <span
      className={classes.tile}
      data-testid="star-tile"
      data-active={active ? "true" : "false"}
      style={{ backgroundColor: color }}
      aria-hidden="true"
    >
      <svg
        className={classes.glyph}
        viewBox="0 0 24 24"
        fill={RATING_COLORS.starGlyph}
        focusable="false"
      >
        <path d={STAR_PATH} />
      </svg>
    </span>
  );
}

function starLabel(n) {
  return `${n} star${n === 1 ? "" : "s"}`;
}

export default function StarTiles({
  value = 0,
  onChange,
  size = 48,
  mobileSize,
  gap = 4,
  readOnly = false,
  name = "stars",
  ariaLabel = "Rate your experience",
}) {
  const classes = useStyles();
  const [hovered, setHovered] = useState(0);

  const selected = Number(value) || 0;
  const shown = !readOnly && hovered > 0 ? hovered : selected;
  const tierColor = starTierColor(shown);

  const groupStyle = {
    "--star-tile-size": `${size}px`,
    "--star-tile-size-mobile": `${mobileSize || size}px`,
    gap: `${gap}px`,
  };

  const tiles = STARS.map((n) => {
    const active = shown >= n;
    return (
      <Tile
        key={n}
        classes={classes}
        active={active}
        color={active ? tierColor : RATING_COLORS.starEmpty}
      />
    );
  });

  if (readOnly) {
    return (
      <div
        role="img"
        aria-label={`${selected} out of ${STAR_COUNT} stars`}
        className={classes.group}
        style={groupStyle}
      >
        {tiles}
      </div>
    );
  }

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={classes.group}
      style={groupStyle}
      onMouseLeave={() => setHovered(0)}
    >
      {STARS.map((n, i) => (
        <label
          key={n}
          className={classes.label}
          onMouseEnter={() => setHovered(n)}
        >
          <input
            type="radio"
            className={classes.input}
            name={name}
            value={n}
            checked={selected === n}
            onChange={() => onChange && onChange(n)}
            aria-label={starLabel(n)}
          />
          {tiles[i]}
        </label>
      ))}
    </div>
  );
}
